/**
 * SecuritySection Component
 * Connected accounts (OAuth), password management, and add-password for OAuth-only users.
 */

import { useOAuth } from '@hooks/useOAuth.ts';
import type { LinkedOAuthProvider } from '@parlay-picker/shared/oauth.ts';
import { AnimatePresence, motion } from 'framer-motion';
import { Link as LinkIcon, Loader2, Unlink } from 'lucide-react';
import type { ReactElement } from 'react';
import { useState } from 'react';

interface SecuritySectionProps {
  onChangePassword: (
    current: string,
    newPassword: string,
    confirmPassword: string,
  ) => Promise<void>;
}

const PROVIDER_LABELS: Record<string, string> = {
  google: 'Google',
  apple: 'Apple',
};

function ProviderIcon({ provider }: { provider: string }): ReactElement {
  if (provider === 'google') {
    return (
      <svg className="w-4 h-4" viewBox="0 0 24 24">
        <path
          fill="#4285F4"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        />
        <path
          fill="#34A853"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
          fill="#FBBC05"
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
          fill="#EA4335"
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
      </svg>
    );
  }
  // Apple icon
  return (
    <svg className="w-4 h-4 fill-current text-theme-text" viewBox="0 0 24 24">
      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}

function ConnectedAccountRow({
  linked,
  onUnlink,
  unlinking,
}: {
  linked: LinkedOAuthProvider;
  onUnlink: () => void;
  unlinking: boolean;
}): ReactElement {
  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center gap-3">
        <ProviderIcon provider={linked.provider} />
        <div>
          <p className="text-sm text-theme-text font-medium">
            {PROVIDER_LABELS[linked.provider] || linked.provider}
          </p>
          <p className="text-xs text-theme-text-tertiary">{linked.providerEmail}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={onUnlink}
        disabled={unlinking}
        className="flex items-center gap-1 px-2.5 py-1 text-xs text-theme-danger hover:bg-theme-danger/10 rounded transition-colors disabled:opacity-50"
      >
        {unlinking ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unlink className="w-3 h-3" />}
        Unlink
      </button>
    </div>
  );
}

export function SecuritySection({ onChangePassword }: SecuritySectionProps): ReactElement {
  const {
    providers,
    hasPassword,
    loading: oauthLoading,
    error: oauthError,
    unlinkProvider,
    addPassword,
  } = useOAuth();

  // Password change state
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  // Add password state (for OAuth-only users)
  const [isAddingPassword, setIsAddingPassword] = useState(false);
  const [addNewPassword, setAddNewPassword] = useState('');
  const [addConfirmPassword, setAddConfirmPassword] = useState('');
  const [savingNewPassword, setSavingNewPassword] = useState(false);
  const [addPasswordError, setAddPasswordError] = useState<string | null>(null);

  // Unlink state
  const [unlinkingProvider, setUnlinkingProvider] = useState<string | null>(null);
  const [unlinkError, setUnlinkError] = useState<string | null>(null);

  const linkedProviders = providers.filter((p) => p.provider);
  const unlinkedProviders = (['google', 'apple'] as const).filter(
    (p) => !linkedProviders.some((lp) => lp.provider === p),
  );

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) return;
    setSavingPassword(true);
    try {
      await onChangePassword(currentPassword, newPassword, confirmPassword);
      setIsChangingPassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch {
      // Error handled by parent
    } finally {
      setSavingPassword(false);
    }
  };

  const handleAddPassword = async () => {
    if (!addNewPassword || !addConfirmPassword) return;
    if (addNewPassword.length < 8) {
      setAddPasswordError('Must be at least 8 characters');
      return;
    }
    if (addNewPassword !== addConfirmPassword) {
      setAddPasswordError('Passwords do not match');
      return;
    }
    setSavingNewPassword(true);
    setAddPasswordError(null);
    const result = await addPassword(addNewPassword, addConfirmPassword);
    if (result.success) {
      setIsAddingPassword(false);
      setAddNewPassword('');
      setAddConfirmPassword('');
    } else {
      setAddPasswordError(result.error || 'Failed to add password');
    }
    setSavingNewPassword(false);
  };

  const handleUnlink = async (provider: string) => {
    setUnlinkingProvider(provider);
    setUnlinkError(null);
    const result = await unlinkProvider(provider as 'google' | 'apple');
    if (!result.success) {
      setUnlinkError(result.error || 'Failed to unlink');
    }
    setUnlinkingProvider(null);
  };

  const handleLinkOAuth = async (provider: 'google' | 'apple') => {
    // Google: use Google Identity Services
    if (provider === 'google') {
      const google = (window as unknown as Record<string, unknown>).google as
        | {
            accounts: {
              id: { initialize: (cfg: Record<string, unknown>) => void; prompt: () => void };
            };
          }
        | undefined;
      if (!google?.accounts?.id) {
        setUnlinkError('Google Sign-In is not available. Please try again later.');
        return;
      }
      // Prompt will be handled by the global callback set in AuthPage/index.html
      // For settings linking, we need a password-verified flow
      // This is handled via the link modal in a future iteration
      setUnlinkError('To link Google, please use the login page first, then link from settings.');
      return;
    }
    // Apple: similar limitation
    setUnlinkError('To link Apple, please use the login page first, then link from settings.');
  };

  return (
    <section className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-medium text-theme-text">Security</h3>
      </div>

      {/* Connected Accounts */}
      <div className="bg-theme-surface rounded-lg p-4 border border-theme-border mb-3">
        <div className="flex items-center gap-2 mb-3">
          <LinkIcon className="w-4 h-4 text-theme-text-secondary" />
          <p className="text-theme-text font-medium text-sm">Connected Accounts</p>
        </div>

        {oauthLoading ? (
          <div className="flex items-center gap-2 py-2">
            <Loader2 className="w-4 h-4 animate-spin text-theme-text-tertiary" />
            <span className="text-sm text-theme-text-tertiary">Loading...</span>
          </div>
        ) : (
          <>
            {linkedProviders.length > 0 && (
              <div className="divide-y divide-theme-border">
                {linkedProviders.map((lp) => (
                  <ConnectedAccountRow
                    key={lp.provider}
                    linked={lp}
                    onUnlink={() => handleUnlink(lp.provider)}
                    unlinking={unlinkingProvider === lp.provider}
                  />
                ))}
              </div>
            )}

            {unlinkedProviders.length > 0 && (
              <div
                className={
                  linkedProviders.length > 0 ? 'mt-3 pt-3 border-t border-theme-border' : ''
                }
              >
                <div className="flex flex-wrap gap-2">
                  {unlinkedProviders.map((provider) => (
                    <button
                      key={provider}
                      type="button"
                      onClick={() => handleLinkOAuth(provider)}
                      className="flex items-center gap-2 px-3 py-1.5 bg-theme-bg hover:bg-theme-border text-theme-text text-sm rounded transition-colors"
                    >
                      <ProviderIcon provider={provider} />
                      Link {PROVIDER_LABELS[provider]}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {linkedProviders.length === 0 && unlinkedProviders.length === 0 && (
              <p className="text-sm text-theme-text-tertiary py-1">No social accounts available</p>
            )}

            {(oauthError || unlinkError) && (
              <p className="text-xs text-theme-danger mt-2">{oauthError || unlinkError}</p>
            )}
          </>
        )}
      </div>

      {/* Password Section */}
      <div className="bg-theme-surface rounded-lg p-4 border border-theme-border overflow-hidden">
        <AnimatePresence mode="wait" initial={false}>
          {hasPassword ? (
            // User HAS a password — show change password
            isChangingPassword ? (
              <motion.div
                key="password-form"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
                className="space-y-3"
              >
                <div>
                  <label className="block text-xs text-theme-text-tertiary mb-1">
                    Current Password
                  </label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-3 py-2 bg-theme-bg border border-theme-border rounded text-theme-text text-sm focus:outline-none focus:border-theme-primary"
                  />
                </div>
                <div>
                  <label className="block text-xs text-theme-text-tertiary mb-1">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 bg-theme-bg border border-theme-border rounded text-theme-text text-sm focus:outline-none focus:border-theme-primary"
                  />
                </div>
                <div>
                  <label className="block text-xs text-theme-text-tertiary mb-1">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 bg-theme-bg border border-theme-border rounded text-theme-text text-sm focus:outline-none focus:border-theme-primary"
                  />
                </div>
                {newPassword && newPassword.length < 8 && (
                  <p className="text-xs text-theme-warning">Must be at least 8 characters</p>
                )}
                {confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-xs text-theme-danger">Passwords do not match</p>
                )}
                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={handleChangePassword}
                    disabled={
                      savingPassword ||
                      !currentPassword ||
                      !newPassword ||
                      !confirmPassword ||
                      newPassword.length < 8 ||
                      newPassword !== confirmPassword
                    }
                    className="px-3 py-1.5 bg-theme-primary-fill hover:bg-theme-primary-fill-hover disabled:bg-theme-surface disabled:text-theme-text-tertiary text-theme-on-primary-fill text-sm font-medium rounded transition-colors"
                  >
                    {savingPassword ? 'Changing...' : 'Change Password'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsChangingPassword(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                    }}
                    disabled={savingPassword}
                    className="px-3 py-1.5 bg-theme-bg hover:bg-theme-border text-theme-text-secondary text-sm font-medium rounded transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="password-display"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="flex items-center justify-between"
              >
                <div>
                  <p className="text-theme-text font-medium">Password</p>
                  <p className="text-theme-text-secondary text-sm">Change your account password</p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsChangingPassword(true)}
                  className="px-3 py-1.5 bg-theme-bg hover:bg-theme-border text-theme-text text-sm font-medium rounded transition-colors"
                >
                  Change
                </button>
              </motion.div>
            )
          ) : // User does NOT have a password (OAuth-only) — show add password
          isAddingPassword ? (
            <motion.div
              key="add-password-form"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              className="space-y-3"
            >
              <div>
                <label className="block text-xs text-theme-text-tertiary mb-1">New Password</label>
                <input
                  type="password"
                  value={addNewPassword}
                  onChange={(e) => setAddNewPassword(e.target.value)}
                  className="w-full px-3 py-2 bg-theme-bg border border-theme-border rounded text-theme-text text-sm focus:outline-none focus:border-theme-primary"
                />
              </div>
              <div>
                <label className="block text-xs text-theme-text-tertiary mb-1">
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={addConfirmPassword}
                  onChange={(e) => setAddConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 bg-theme-bg border border-theme-border rounded text-theme-text text-sm focus:outline-none focus:border-theme-primary"
                />
              </div>
              {addNewPassword && addNewPassword.length < 8 && (
                <p className="text-xs text-theme-warning">Must be at least 8 characters</p>
              )}
              {addConfirmPassword && addNewPassword !== addConfirmPassword && (
                <p className="text-xs text-theme-danger">Passwords do not match</p>
              )}
              {addPasswordError && <p className="text-xs text-theme-danger">{addPasswordError}</p>}
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleAddPassword}
                  disabled={
                    savingNewPassword ||
                    !addNewPassword ||
                    !addConfirmPassword ||
                    addNewPassword.length < 8 ||
                    addNewPassword !== addConfirmPassword
                  }
                  className="px-3 py-1.5 bg-theme-primary-fill hover:bg-theme-primary-fill-hover disabled:bg-theme-surface disabled:text-theme-text-tertiary text-theme-on-primary-fill text-sm font-medium rounded transition-colors"
                >
                  {savingNewPassword ? 'Adding...' : 'Add Password'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsAddingPassword(false);
                    setAddNewPassword('');
                    setAddConfirmPassword('');
                    setAddPasswordError(null);
                  }}
                  disabled={savingNewPassword}
                  className="px-3 py-1.5 bg-theme-bg hover:bg-theme-border text-theme-text-secondary text-sm font-medium rounded transition-colors"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="add-password-display"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="flex items-center justify-between"
            >
              <div>
                <p className="text-theme-text font-medium">Password</p>
                <p className="text-theme-text-secondary text-sm">
                  Add a password to sign in without social accounts
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsAddingPassword(true)}
                className="px-3 py-1.5 bg-theme-primary-fill hover:bg-theme-primary-fill-hover text-theme-on-primary-fill text-sm font-medium rounded transition-colors"
              >
                Add
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
